import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, FileText, Users, ListChecks, Target, Eye, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { STEPS, generateRecurringDates } from "../components/wizard/WizardConstants";
import { WizardStepTemplate } from "../components/wizard/WizardStepTemplate";
import { WizardStepDetails } from "../components/wizard/WizardStepDetails";
import { WizardStepDeliverables } from "../components/wizard/WizardStepDeliverables";
import { WizardStepKPIs } from "../components/wizard/WizardStepKPIs";
import { WizardStepReview } from "../components/wizard/WizardStepReview";

const STEP_ICONS = [FileText, Users, ListChecks, Target, Eye];

const INITIAL_FORM = {
  title: "", contract_type: "", contract_value: "", currency: "",
  effective_date: "", expiration_date: "", department: "", spend_category: "",
  supplier_unique_id: "", contract_owner_name: "", contract_owner_email: "",
  content: "", contract_scope: "",
  contracting_party: { legal_name: "", registered_address: "", contact_email: "", contact_phone: "" },
  sections: [],
  deliverables: [{ description: "", delivery_date: "", recurrence: "once" }],
  kpis: [{ description: "", measure: "" }],
  template_id: null, template_metadata: null,
};

export default function ContractCreationWizard() {
  const navigate = useNavigate();
  const { hasFeature } = useAuth();
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedContractId, setSavedContractId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  // Supplier search state (lifted so parent can coordinate duplicate check)
  const [supplierQuery, setSupplierQuery] = useState("");
  const [supplierResults, setSupplierResults] = useState([]);
  const [supplierSearching, setSupplierSearching] = useState(false);
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const [supplierConfirmed, setSupplierConfirmed] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  useEffect(() => {
    apiClient.get("/templates?template_type=contract").then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  // ── Field setter ──
  const set = useCallback((field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  // ── Supplier selection ──
  const selectSupplier = useCallback((supplier) => {
    setSupplierQuery(supplier.name);
    set("contracting_party.legal_name", supplier.name);
    if (supplier.email) set("contracting_party.contact_email", supplier.email);
    if (supplier.phone) set("contracting_party.contact_phone", supplier.phone);
    if (supplier.address) set("contracting_party.registered_address", supplier.address);
    if (supplier.supplier_id) set("supplier_unique_id", supplier.supplier_id);
    setSupplierConfirmed(true);
    setSupplierDropdownOpen(false);
    toast.success(`Supplier "${supplier.name}" selected`);
  }, [set]);

  // ── Template selection ──
  const selectTemplate = useCallback((tmpl) => {
    const sections = (tmpl.sections || []).map(s => ({ ...s }));
    const content = sections.map(s => `## ${s.name}\n\n${s.content}`).join("\n\n---\n\n");
    setForm(prev => ({
      ...prev, template_id: tmpl.id, sections, content,
      contract_type: prev.contract_type || tmpl.category,
      template_metadata: tmpl.template_metadata || null,
    }));
    toast.success(`Template "${tmpl.name}" selected`);
    setStep(1);
  }, []);

  // ── Deliverable/KPI CRUD ──
  const addDeliverable = () => setForm(prev => ({ ...prev, deliverables: [...prev.deliverables, { description: "", delivery_date: "", recurrence: "once" }] }));
  const removeDeliverable = (i) => setForm(prev => ({ ...prev, deliverables: prev.deliverables.filter((_, idx) => idx !== i) }));
  const updateDeliverable = (i, field, value) => setForm(prev => { const d = [...prev.deliverables]; d[i] = { ...d[i], [field]: value }; return { ...prev, deliverables: d }; });
  const addKpi = () => setForm(prev => ({ ...prev, kpis: [...prev.kpis, { description: "", measure: "" }] }));
  const removeKpi = (i) => setForm(prev => ({ ...prev, kpis: prev.kpis.filter((_, idx) => idx !== i) }));
  const updateKpi = (i, field, value) => setForm(prev => { const k = [...prev.kpis]; k[i] = { ...k[i], [field]: value }; return { ...prev, kpis: k }; });

  // ── Placeholder replacement ──
  const fillPlaceholders = useCallback((text) => {
    if (!text) return text;
    const fmtDate = (d) => { if (!d) return ""; try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); } catch { return d; } };
    const fmtVal = () => { if (!form.contract_value) return ""; const ccy = form.currency || ""; try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy || "GBP", minimumFractionDigits: 0 }).format(parseFloat(form.contract_value)); } catch { return `${ccy} ${Number(form.contract_value).toLocaleString()}`; } };
    const p = form.contracting_party;
    const replacements = [
      [/\[insert\s*date\]/gi, fmtDate(form.effective_date) || "[insert date]"],
      [/\[effective\s*date\]/gi, fmtDate(form.effective_date) || "[effective date]"],
      [/\[commencement\s*date\]/gi, fmtDate(form.effective_date) || "[commencement date]"],
      [/\[start\s*date\]/gi, fmtDate(form.effective_date) || "[start date]"],
      [/\[expiry\s*date\]/gi, fmtDate(form.expiration_date) || "[expiry date]"],
      [/\[expiration\s*date\]/gi, fmtDate(form.expiration_date) || "[expiration date]"],
      [/\[end\s*date\]/gi, fmtDate(form.expiration_date) || "[end date]"],
      [/\[termination\s*date\]/gi, fmtDate(form.expiration_date) || "[termination date]"],
      [/\[SERVICE PROVIDER LEGAL NAME\]/g, p.legal_name || "[SERVICE PROVIDER LEGAL NAME]"],
      [/\[service provider(?: legal)? name\]/gi, p.legal_name || "[service provider name]"],
      [/\[supplier(?: legal)? name\]/gi, p.legal_name || "[supplier name]"],
      [/\[vendor(?: legal)? name\]/gi, p.legal_name || "[vendor name]"],
      [/\[contractor(?: legal)? name\]/gi, p.legal_name || "[contractor name]"],
      [/\[provider(?: legal)? name\]/gi, p.legal_name || "[provider name]"],
      [/\[counterparty(?: legal)? name\]/gi, p.legal_name || "[counterparty name]"],
      [/\[service provider(?:'s)? address\]/gi, p.registered_address || "[service provider address]"],
      [/\[supplier(?:'s)? address\]/gi, p.registered_address || "[supplier address]"],
      [/\[vendor(?:'s)? address\]/gi, p.registered_address || "[vendor address]"],
      [/\[contractor(?:'s)? address\]/gi, p.registered_address || "[contractor address]"],
      [/\[CLIENT LEGAL NAME\]/g, form.contract_owner_name || "[CLIENT LEGAL NAME]"],
      [/\[client(?: legal)? name\]/gi, form.contract_owner_name || "[client name]"],
      [/\[buyer(?: legal)? name\]/gi, form.contract_owner_name || "[buyer name]"],
      [/\[company(?: legal)? name\]/gi, form.contract_owner_name || "[company name]"],
      [/\[supplier(?:'s)? email\]/gi, p.contact_email || "[supplier email]"],
      [/\[supplier(?:'s)? phone\]/gi, p.contact_phone || "[supplier phone]"],
      [/\[contract value\]/gi, fmtVal() || "[contract value]"],
      [/\[total value\]/gi, fmtVal() || "[total value]"],
      [/\[agreement value\]/gi, fmtVal() || "[agreement value]"],
      [/\[address\]/gi, p.registered_address || "[address]"],
      [/ABN\/ACN\s*\[insert\]/gi, ""],
    ];
    let result = text;
    for (const [pat, rep] of replacements) result = result.replace(pat, rep);
    return result;
  }, [form]);

  // ── Payload ──
  const buildPayload = useCallback((statusLabel = "draft") => {
    const isFinal = statusLabel === "draft";
    const proc = (t) => isFinal ? fillPlaceholders(t) : t;
    return {
      title: form.title, contract_type: form.contract_type,
      contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
      currency: form.currency || null, effective_date: form.effective_date,
      expiration_date: form.expiration_date, department: form.department,
      spend_category: form.spend_category, supplier_unique_id: form.supplier_unique_id,
      content: proc(form.content), contracting_party: form.contracting_party,
      sections: form.sections.map(s => ({ ...s, content: proc(s.content) })),
      additional_clauses: [],
      custom_fields: {
        contract_owner_name: form.contract_owner_name, contract_owner_email: form.contract_owner_email,
        wizard_status: statusLabel, contract_scope: form.contract_scope || "",
        deliverables: form.deliverables.filter(d => d.description).flatMap(d => {
          if (d.recurrence && d.recurrence !== "once" && d.delivery_date) {
            const dates = generateRecurringDates(d.delivery_date, d.recurrence, d.recurrence_end || form.expiration_date);
            return dates.map((date, idx) => ({ description: d.description, delivery_date: date, recurrence: d.recurrence, occurrence: idx + 1, total_occurrences: dates.length }));
          }
          return [{ description: d.description, delivery_date: d.delivery_date, recurrence: "once" }];
        }),
        kpis: form.kpis.filter(k => k.description),
        template_id: form.template_id, template_metadata: form.template_metadata,
      },
    };
  }, [form, fillPlaceholders]);

  // ── Save ──
  const handleAutoSave = useCallback(async () => {
    if (!form.title || !form.contracting_party.legal_name) return;
    try {
      const payload = buildPayload("pre-draft");
      if (savedContractId) { await apiClient.put(`/contracts/${savedContractId}`, payload); }
      else { const res = await apiClient.post("/contracts", payload); setSavedContractId(res.data.id); toast.success("Pre-draft saved", { duration: 3000 }); }
    } catch { toast.error("Auto-save failed", { duration: 2000 }); }
  }, [form, savedContractId, buildPayload]);

  const handleSave = async () => {
    if (!form.title) { toast.error("Contract title is required"); return; }
    if (!form.currency) { toast.error("Currency is required"); return; }
    setSaving(true);
    try {
      const payload = buildPayload("draft");
      let id = savedContractId;
      if (id) { await apiClient.put(`/contracts/${id}`, payload); }
      else { const res = await apiClient.post("/contracts", payload); id = res.data.id; }
      toast.success("Contract saved as draft!");
      navigate(`/contracts/${id}/edit`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  // ── Validation ──
  const canProceed = () => {
    if (step === 1) return form.title && form.contract_type && form.currency && form.contracting_party.legal_name && form.contracting_party.contact_email && form.contracting_party.contact_phone;
    return true;
  };
  const stepIssues = useCallback(() => {
    const issues = [];
    if (!form.title) issues.push("Contract title");
    if (!form.contract_type) issues.push("Contract type");
    if (!form.currency) issues.push("Currency");
    if (!form.contracting_party.legal_name) issues.push("Supplier name");
    if (!form.contracting_party.contact_email) issues.push("Supplier email");
    if (!form.contracting_party.contact_phone) issues.push("Supplier phone");
    if (!form.effective_date) issues.push("Effective date");
    return issues;
  }, [form]);

  // ── Navigation ──
  const checkSupplierDuplicate = async () => {
    const name = form.contracting_party.legal_name?.trim();
    if (!name) return true;
    try {
      const res = await apiClient.get(`/suppliers/search?q=${encodeURIComponent(name)}`);
      const matches = (res.data || []).filter(s => {
        const a = s.name.toLowerCase().replace(/[^a-z0-9]/g, "");
        const b = name.toLowerCase().replace(/[^a-z0-9]/g, "");
        return a === b || a.includes(b) || b.includes(a);
      });
      if (matches.length > 0) { setDuplicateMatches(matches); setDuplicateDialogOpen(true); return false; }
    } catch { /* proceed */ }
    return true;
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!supplierConfirmed) { const ok = await checkSupplierDuplicate(); if (!ok) return; }
      await handleAutoSave();
    }
    setStep(step + 1);
  };

  const confirmNewSupplier = async () => {
    setSupplierConfirmed(true); setDuplicateDialogOpen(false); setDuplicateMatches([]);
    await handleAutoSave(); setStep(step + 1);
  };

  // ── Supplier state bundle ──
  const supplierState = {
    supplierQuery, setSupplierQuery, supplierResults, setSupplierResults,
    supplierSearching, setSupplierSearching, supplierDropdownOpen, setSupplierDropdownOpen,
    supplierConfirmed, setSupplierConfirmed, selectSupplier,
  };

  return (
    <>
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="contract-wizard">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/contracts")} className="h-9 w-9 rounded-sm" data-testid="wizard-back"><ArrowLeft size={18} /></Button>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Create New Contract</h1>
          <p className="text-sm text-slate-500">Step-by-step guided creation</p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-1" data-testid="wizard-steps">
        {STEPS.map((s, i) => {
          const Icon = STEP_ICONS[i];
          return (
            <React.Fragment key={s.id}>
              <button
                className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-all ${i === step ? "bg-slate-900 text-white dark:bg-cyan-500 dark:text-slate-900 font-medium" : i < step ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-50 text-slate-400 border border-slate-200"}`}
                onClick={() => i <= step && setStep(i)} disabled={i > step} data-testid={`wizard-step-${s.id}`}
              >
                {i < step ? <Check size={14} /> : <Icon size={14} />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`w-6 h-px ${i < step ? "bg-emerald-300" : "bg-slate-200"}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {step === 0 && <WizardStepTemplate form={form} setForm={setForm} templates={templates} hasFeature={hasFeature} onSelectTemplate={selectTemplate} onStepChange={setStep} />}
          {step === 1 && <WizardStepDetails form={form} set={set} supplierState={supplierState} />}
          {step === 2 && <WizardStepDeliverables form={form} updateDeliverable={updateDeliverable} addDeliverable={addDeliverable} removeDeliverable={removeDeliverable} />}
          {step === 3 && <WizardStepKPIs form={form} updateKpi={updateKpi} addKpi={addKpi} removeKpi={removeKpi} />}
          {step === 4 && <WizardStepReview form={form} stepIssues={stepIssues} />}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => step > 0 ? setStep(step - 1) : navigate("/contracts")} className="rounded-sm gap-1.5" data-testid="wizard-prev">
          <ArrowLeft size={14} /> {step === 0 ? "Cancel" : "Back"}
        </Button>
        <div className="flex gap-2">
          {step === 4 && (
            <Button variant="outline" onClick={() => handleSave()} disabled={saving || stepIssues().length > 0} className="rounded-sm gap-1.5" data-testid="wizard-save-draft">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save as Draft
            </Button>
          )}
          {step < 4 && (
            <Button onClick={handleNext} disabled={step === 1 && !canProceed()} className="rounded-sm gap-1.5 bg-slate-900" data-testid="wizard-next">
              Next <ArrowRight size={14} />
            </Button>
          )}
        </div>
      </div>
    </div>

    {/* Duplicate Supplier Dialog */}
    <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle size={18} />Similar supplier already exists</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600"><span className="font-medium">"{form.contracting_party.legal_name}"</span> looks similar to an existing supplier.</p>
          <div className="border rounded-sm divide-y max-h-48 overflow-y-auto" data-testid="duplicate-supplier-list">
            {duplicateMatches.map((s, i) => (
              <button key={`dup-${s.name}-${i}`} className="w-full text-left px-3 py-2.5 hover:bg-cyan-50 transition-colors" onClick={() => { selectSupplier(s); setDuplicateDialogOpen(false); setDuplicateMatches([]); }} data-testid={`duplicate-option-${i}`}>
                <p className="text-sm font-medium">{s.name}</p>
                <p className="text-[10px] text-slate-400">{s.supplier_id && <span className="font-mono text-cyan-600">{s.supplier_id} · </span>}{s.email && `${s.email} · `}{s.contract_count} existing contract{s.contract_count !== 1 ? "s" : ""}</p>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" className="rounded-sm gap-1.5 flex-1" onClick={confirmNewSupplier} data-testid="confirm-new-supplier-btn">This is a different supplier — proceed</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
